function escapeHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Keep source characters attached to their offsets while replacing delimiters
// with HTML. Rendering and annotation anchors therefore share the same rules,
// including an unfinished delimiter becoming formatting as streaming continues.
function wrapInline(chars, pattern, prefixLength, delimiterLength, tag) {
  const text = chars.map((entry) => entry.char).join("");
  const result = [];
  const markup = (value) => value.split("").map((char) => ({ char, source: null }));
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    const open = match.index + prefixLength(match);
    const end = match.index + match[0].length;
    for (let index = cursor; index < open; index += 1) result.push(chars[index]);
    result.push(...markup(`<${tag}>`));
    for (let index = open + delimiterLength; index < end - delimiterLength; index += 1) result.push(chars[index]);
    result.push(...markup(`</${tag}>`));
    cursor = end;
  }
  return [...result, ...chars.slice(cursor)];
}

function parseSampleProse(source) {
  const blocks = [];
  const rendered = [];
  for (const block of source.matchAll(/[^\n]+/g)) {
    const leading = block[0].length - block[0].trimStart().length;
    const value = block[0].trim();
    if (!value || /^(-{3,}|\*{3,}|_{3,})$/.test(value)) continue;
    const heading = value.match(/^#{1,6}\s+(.*)$/);
    const headingLength = heading ? value.length - heading[1].length : 0;
    let chars = Array.from({ length: value.length - headingLength }, (_, index) => ({
      char: value[index + headingLength], source: block.index + leading + index + headingLength,
    }));
    chars = wrapInline(chars, /\*\*([^*]+)\*\*/g, () => 0, 2, "strong");
    chars = wrapInline(chars, /(^|[^*])\*([^*\n]+)\*/g, (match) => match[1].length, 1, "em");
    const html = chars.map((entry) => entry.source === null ? entry.char : escapeHtml(entry.char)).join("");
    blocks.push(heading ? `<h3 class="scene-heading">${html}</h3>` : `<p>${html}</p>`);
    for (const entry of chars) if (entry.source !== null) rendered.push(entry);
  }
  return {
    html: blocks.join(""),
    text: rendered.map((entry) => entry.char).join(""),
    sourceOffsets: rendered.map((entry) => entry.source),
  };
}

export function renderSampleProse(text) {
  return parseSampleProse(text).html;
}

// Annotation coordinates describe this exact rendered body, without UI labels.
export const ANNOTATION_COORDINATE_SPACE = "rendered_prose_v1";

export function annotationHasPosition(text, annotation) {
  return Number.isInteger(annotation.start) && annotation.start >= 0 &&
    Number.isInteger(annotation.end) && annotation.end > annotation.start &&
    annotation.end <= text.length && text.slice(annotation.start, annotation.end) === annotation.text;
}

function htmlText(html) {
  const body = document.createElement("div");
  body.innerHTML = html;
  return body.textContent || "";
}

export function renderedProseText(text) {
  return htmlText(parseSampleProse(text).html);
}

export function renderedProseMap(source) {
  const { html, text, sourceOffsets } = parseSampleProse(source);
  // Browser normalization (for example a raw carriage return) can remove a
  // source character. Do not invent annotation coordinates in that case.
  if (text !== htmlText(html)) return null;
  return { text, sourceOffsets };
}

export function anchorAnnotation(source, annotation) {
  const map = renderedProseMap(source);
  if (!map || map.text.slice(annotation.start, annotation.end) !== annotation.text) return annotation;
  return { ...annotation, sourceStart: map.sourceOffsets[annotation.start], sourceEnd: map.sourceOffsets[annotation.end - 1] + 1 };
}

export function resolveSourceAnnotation(source, annotation) {
  const map = renderedProseMap(source);
  if (!map) return annotation;
  if (Number.isInteger(annotation.sourceStart) && Number.isInteger(annotation.sourceEnd)) {
    const start = map.sourceOffsets.findIndex((offset) => offset >= annotation.sourceStart);
    let end = map.sourceOffsets.findIndex((offset) => offset >= annotation.sourceEnd);
    if (end < 0) end = map.text.length;
    if (start >= 0 && end > start) return { ...annotation, start, end, text: map.text.slice(start, end) };
  }
  // Legacy local drafts have no source anchors. Only retain an exact match;
  // guessing the closest duplicate would silently attach feedback elsewhere.
  return annotation;
}
